import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";

const blankPart = () => ({ id: null, thai: "", english: "", vietnamese: "", myanmar: "" });

const capitalizeFirst = (value = "") => {
    const trimmed = value.trim();
    return trimmed ? `${trimmed.charAt(0).toUpperCase()}${trimmed.slice(1)}` : "";
};

export default function PartSetupSection({ data, onReload, apiBase }) {
    const [options, setOptions] = useState({ product_categories: [], styles: [] });
    const [productCategory, setProductCategory] = useState("");
    const [styleFamily, setStyleFamily] = useState("");
    const [parts, setParts] = useState([]);
    const [removedIds, setRemovedIds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        axios.post(`${apiBase}/api/endline2/setup/part-options`)
            .then(({ data: response }) => {
                const next = response || { product_categories: [], styles: [] };
                setOptions(next);
                setProductCategory(next.product_categories?.[0] || "");
            })
            .catch((error) => Swal.fire({ icon: "error", title: "Load part setup failed", text: error.response?.data?.message || error.message }))
            .finally(() => setLoading(false));
    }, [apiBase]);

    const styleOptions = useMemo(
        () => options.styles.filter((item) => item.product_category === productCategory),
        [options.styles, productCategory],
    );
    const selectedStyle = useMemo(
        () => styleOptions.find((item) => item.style_family === styleFamily) || null,
        [styleOptions, styleFamily],
    );

    useEffect(() => {
        setStyleFamily(styleOptions[0]?.style_family || "");
    }, [productCategory, styleOptions]);

    useEffect(() => {
        const rows = (data.parts || []).filter(
            (part) => part.product_category === productCategory
                && part.style_family === styleFamily,
        );
        setParts(rows.map((part) => ({
            id: part.id,
            thai: part.defect_part_thai || "",
            english: part.defect_part_eng || "",
            vietnamese: part.defect_part_vi || "",
            myanmar: part.defect_part_my || "",
        })));
        setRemovedIds([]);
    }, [data.parts, productCategory, styleFamily]);

    const updatePart = (index, field, value) => setParts((current) => current.map((part, position) => position === index ? { ...part, [field]: value } : part));
    const removePart = (index) => setParts((current) => {
        const removed = current[index];
        if (removed?.id) setRemovedIds((ids) => [...ids, removed.id]);
        return current.filter((_, position) => position !== index);
    });
    const saveParts = async () => {
        setSaving(true);
        Swal.fire({ title: "Saving parts...", text: "Please wait...", allowOutsideClick: false, allowEscapeKey: false, didOpen: () => Swal.showLoading() });
        try {
            await axios.post(`${apiBase}/api/endline2/setup/parts`, {
                product_category: productCategory,
                style_family: styleFamily,
                parts: parts.map((part) => ({
                    ...part,
                    english: capitalizeFirst(part.english),
                })),
                removed_ids: removedIds,
            });
            await onReload();
            setRemovedIds([]);
            Swal.close();
            await Swal.fire({ icon: "success", title: "Parts saved", timer: 900, showConfirmButton: false });
        } catch (error) {
            Swal.close();
            Swal.fire({ icon: "error", title: "Save parts failed", text: error.response?.data?.message || error.message });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="setup-loading">Loading part setup...</div>;

    return (
        <section className="part-setup-panel">
            <header className="part-setup-head"><div><span>Garment profile</span><h2>Defect parts</h2></div><button className="setup-primary" type="button" disabled={!productCategory || !styleFamily || saving} onClick={saveParts}>{saving ? "Saving..." : "Save all parts"}</button></header>
            <div className="part-selector-grid">
                <label>Product category<select value={productCategory} onChange={(event) => setProductCategory(event.target.value)}>{options.product_categories.map((item) => <option key={item} value={item}>{item}</option>)}</select></label>
                <label>Style family<select value={styleFamily} onChange={(event) => setStyleFamily(event.target.value)}>{styleOptions.map((item) => <option key={item.style_family} value={item.style_family}>{item.style_family}</option>)}</select></label>
            </div>
            <div className="part-workspace">
                <article className="part-editor-panel">
                    <header><div><h3>Parts in {productCategory || "product category"} / {styleFamily || "style family"}</h3><span>{parts.length} part{parts.length === 1 ? "" : "s"}</span></div><button type="button" className="part-add-button" onClick={() => setParts((current) => [...current, blankPart()])}><FontAwesomeIcon icon={faPlus} /> Add part</button></header>
                    <div className="part-language-head"><span>Thai</span><span>English</span><span>Vietnamese</span><span>Myanmar</span><i /></div>
                    <div className="part-row-list">
                        {parts.map((part, index) => <div className="part-language-row" key={part.id || `new-${index}`}>
                            <input aria-label="Thai part name" value={part.thai} onChange={(event) => updatePart(index, "thai", event.target.value)} />
                            <input aria-label="English part name" value={part.english} onChange={(event) => updatePart(index, "english", event.target.value)} />
                            <input aria-label="Vietnamese part name" value={part.vietnamese} onChange={(event) => updatePart(index, "vietnamese", event.target.value)} />
                            <input aria-label="Myanmar part name" value={part.myanmar} onChange={(event) => updatePart(index, "myanmar", event.target.value)} />
                            <button type="button" className="part-delete-button" aria-label="Delete part" onClick={() => removePart(index)}><FontAwesomeIcon icon={faTrash} /></button>
                        </div>)}
                        {!parts.length && <div className="setup-empty">No parts saved for this product category.</div>}
                    </div>
                </article>
                <aside className="part-preview-panel"><header><span>Style family preview</span><strong>{styleFamily || "Select style family"}</strong></header><div className="part-image-frame">{selectedStyle?.image_path ? <div className="part-preview-image"><img src={`/images/Endline-img/${encodeURIComponent(selectedStyle.image_path)}`} alt={styleFamily} style={{ width: "100%", height: "100%", objectFit: "contain", objectPosition: "center" }} onError={(event) => { event.currentTarget.style.visibility = "hidden"; }} /></div> : <span>No matching image file</span>}</div>{selectedStyle?.image_path && <small>{selectedStyle.image_path}</small>}</aside>
            </div>
        </section>
    );
}